import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { Users, Briefcase, TrendingUp, Target } from 'lucide-react';

const RecrutementStats = () => {
  const [stats, setStats] = useState(null);
  const [positionStats, setPositionStats] = useState([]);
  const [evolutionData, setEvolutionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('contacte');
  const [evolutionPeriod, setEvolutionPeriod] = useState('year');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const API = useContext(ApiContext);

  // Récupérer la liste des utilisateurs de l'entreprise
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await API.get('/api/user/company');
        setCompanyUsers(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      }
    };
    fetchUsers();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');

      const userParam = selectedUserId ? `&userId=${selectedUserId}` : '';

      const [globalResponse, positionsResponse, evolutionResponse] = await Promise.all([
        API.get(`/api/recruitment-stats?period=${period}${userParam}`),
        API.get(`/api/recruitment-stats/by-position?period=${period}${userParam}`),
        API.get(`/api/recruitment-stats/evolution?period=${evolutionPeriod}${userParam}`)
      ]);

      setStats(globalResponse.data);

      // Trier les positions : d'abord les ouvertes, puis les fermées, chacune par date de création décroissante
      const sortedPositions = (positionsResponse.data.data || []).sort((a, b) => {
        // Trier par statut open d'abord (true avant false)
        if (a.open !== b.open) {
          return a.open ? -1 : 1;
        }
        // Si même statut, trier par date de création (plus récent d'abord)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setPositionStats(sortedPositions);
      setEvolutionData(evolutionResponse.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des stats de recrutement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period, evolutionPeriod, selectedUserId]);

  const getPeriodLabel = () => {
    switch (period) {
      case 'month':
        return 'Ce mois-ci';
      case 'quarter':
        return 'Ce trimestre';
      case 'year':
        return 'Cette année';
      default:
        return 'Toutes périodes';
    }
  };

  // Formater le mois/année (YYYY-MM -> Mois Année)
  const formatMonthYear = (dateStr) => {
    const [year, month] = dateStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
  };

  // Configuration des métriques
  const metrics = [
    { key: 'contacte', color: '#3b82f6', label: 'Contactés' },
    { key: 'aRepondu', color: '#8b5cf6', label: 'Ont répondu' },
    { key: 'interesse', color: '#10b981', label: 'Intéressés' },
    { key: 'qualification', color: '#06b6d4', label: 'Qualifications' },
    { key: 'cvEnvoye', color: '#f59e0b', label: 'CVs envoyés' },
    { key: 'totalEntretiens', color: '#ef4444', label: 'Entretiens' },
    { key: 'recrute', color: '#22c55e', label: 'Recrutés' },
  ];

  // Tooltip personnalisé pour le graphique d'évolution
  const CustomEvolutionTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-medium text-sm">{formatMonthYear(label)}</p>
          <p className="text-sm" style={{ color: payload[0].color }}>
            {`${payload[0].name}: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Préparer les données pour le graphique en barres
  const chartData = stats
    ? [
        //{ name: 'À contacter', value: stats.stats.aContacter },
        { name: 'Contacté', value: stats.stats.contacte },
        { name: 'A répondu', value: stats.stats.aRepondu },
        { name: 'Pas intéressé', value: stats.stats.pasInteresse },
        { name: 'Intéressé', value: stats.stats.interesse },
        { name: 'Qualification', value: stats.stats.qualification },
        { name: 'CV envoyé', value: stats.stats.cvEnvoye },
        { name: 'Entretien', value: stats.stats.totalEntretiens },
        { name: 'Offre', value: stats.stats.offre },
        { name: 'Recruté', value: stats.stats.recrute },
        { name: 'KO', value: stats.stats.ko }
      ].filter(item => item.value > 0)
    : [];

  // Préparer les données pour le funnel de conversion (COMMENTÉ)
  // const conversionData = stats
  //   ? [
  //       { stage: 'Contactés', count: stats.stats.contacte },
  //       { stage: 'Ont répondu', count: stats.stats.aRepondu },
  //       { stage: 'Intéressés', count: stats.stats.interesse },
  //       { stage: 'Entretiens', count: stats.stats.totalEntretiens },
  //       { stage: 'Recrutés', count: stats.stats.recrute }
  //     ]
  //   : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-blue-600">{`Candidats: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-10 min-h-screen pt-4 sm:pt-8 lg:pt-16">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-slate-200 border-t-indigo-500 rounded-full animate-spin"></div>
          <span className="text-base sm:text-lg">Chargement des statistiques...</span>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="min-h-screen px-2 sm:px-0">
      <header className="mb-4 sm:mb-6 lg:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <p className="text-sm sm:text-base text-slate-600">
            Visualisez vos performances de recrutement.
          </p>
        </div>

        {/* Sélecteurs */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          {/* Sélecteur utilisateur */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Recruteur:</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Tous les recruteurs</option>
              {companyUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sélecteur de période */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Période:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Toutes périodes</option>
              <option value="month">Ce mois-ci</option>
              <option value="quarter">Ce trimestre</option>
              <option value="year">Cette année</option>
            </select>
          </div>
        </div>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Cartes de résumé */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">Total Candidats</h3>
            <Users className="text-blue-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">
            {stats.stats.totalCandidates}
          </p>
          <p className="text-xs text-slate-500 mt-1">{getPeriodLabel()}</p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">Contactés</h3>
            <Target className="text-purple-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-purple-600">
            {stats.stats.contacte}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {stats.stats.aRepondu} réponses ({stats.conversionRates.contacteToRepondu}%)
          </p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">Entretiens</h3>
            <Briefcase className="text-orange-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-orange-600">
            {stats.stats.totalEntretiens}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Dont {stats.stats.entretien} entretien(s) 1
          </p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm sm:text-lg font-medium text-slate-600">Recrutés</h3>
            <TrendingUp className="text-green-500" size={24} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-green-600">
            {stats.stats.recrute}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Taux: {stats.conversionRates.globalConversion}%
          </p>
        </div>
      </div>

      {/* Graphique d'évolution dans le temps */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-3 sm:space-y-0">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800">
            Évolution mensuelle
          </h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            {/* Sélecteur utilisateur pour évolution */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700">Recruteur:</label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Tous</option>
                {companyUsers.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélecteur de période pour évolution */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700">Période:</label>
              <select
                value={evolutionPeriod}
                onChange={(e) => setEvolutionPeriod(e.target.value)}
                className="border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="3months">3 mois</option>
                <option value="6months">6 mois</option>
                <option value="year">12 mois</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sélecteur de métrique */}
        <div className="flex flex-wrap gap-2 mb-4">
          {metrics.map((metric) => (
            <button
              key={metric.key}
              onClick={() => setSelectedMetric(metric.key)}
              className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                selectedMetric === metric.key
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedMetric === metric.key ? { backgroundColor: metric.color } : {}}
            >
              {metric.label}
            </button>
          ))}
        </div>

        {evolutionData.length > 0 ? (
          <div className="h-72 sm:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={evolutionData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="monthYear"
                  tickFormatter={formatMonthYear}
                  tick={{ fontSize: 10 }}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis fontSize={10} />
                <Tooltip content={<CustomEvolutionTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey={selectedMetric}
                  name={metrics.find(m => m.key === selectedMetric)?.label}
                  stroke={metrics.find(m => m.key === selectedMetric)?.color}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-slate-500">
            Aucune donnée d'évolution disponible
          </div>
        )}
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4 sm:gap-6 lg:gap-8 mb-8">
        {/* Répartition par étape */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
            Répartition par étape ({getPeriodLabel()})
          </h2>

          {chartData.length > 0 ? (
            <div className="h-72 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={10}
                  />
                  <YAxis fontSize={10} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* Funnel de conversion - COMMENTÉ */}
        {/* <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
            Funnel de conversion
          </h2>

          {conversionData.length > 0 ? (
            <div className="h-72 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={conversionData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" fontSize={10} angle={-15} textAnchor="end" height={60} />
                  <YAxis fontSize={10} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ r: 5 }}
                    name="Candidats"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Aucune donnée disponible
            </div>
          )}
        </div> */}
      </div>

      {/* Taux de conversion */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border mb-8">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
          Taux de conversion
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Contacté → Répondu</p>
            <p className="text-xl sm:text-2xl font-bold text-indigo-600">
              {stats.conversionRates.contacteToRepondu}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Répondu → Intéressé</p>
            <p className="text-xl sm:text-2xl font-bold text-purple-600">
              {stats.conversionRates.reponduToInteresse}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Intéressé → Entretien</p>
            <p className="text-xl sm:text-2xl font-bold text-orange-600">
              {stats.conversionRates.interesseToEntretien}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Entretien → Recruté</p>
            <p className="text-xl sm:text-2xl font-bold text-green-600">
              {stats.conversionRates.entretienToRecrute}%
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 mb-1">Conversion globale</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-600">
              {stats.conversionRates.globalConversion}%
            </p>
          </div>
        </div>
      </div>

      {/* Statistiques par poste */}
      {positionStats.length > 0 && (
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">
            Performance par poste
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase sticky left-0 bg-slate-50">
                    Poste
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Total
                  </th>
                  {/* <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    À contacter
                  </th> */}
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Contacté
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    A répondu
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Qualification
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    CV envoyé
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Entretiens
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Offre
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    Recrutés
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">
                    KO
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {positionStats.slice(0, 10).map((position) => {
                  const totalRepondu = (position.aRepondu || 0) + (position.pasInteresse || 0) + (position.interesse || 0);
                  return (
                    <tr key={position.positionId} className="hover:bg-slate-50">
                      <td className="px-3 py-3 text-sm text-slate-900 sticky left-0 bg-white">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${position.open ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          <span>{position.partnerName} - {position.positionTitle}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-center font-medium">
                        {position.totalCandidates}
                      </td>
                      {/* <td className="px-3 py-3 text-sm text-center text-slate-600">
                        {position.aContacter || 0}
                      </td> */}
                      <td className="px-3 py-3 text-sm text-center text-blue-600">
                        {position.contacte || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-purple-600">
                        {totalRepondu}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-cyan-600">
                        {position.qualification || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-indigo-600">
                        {position.cvEnvoye || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-orange-600 font-medium">
                        {position.entretiens || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-amber-600">
                        {position.offre || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-green-600 font-medium">
                        {position.recrute || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-red-600 font-medium">
                        {position.ko || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {positionStats.length > 10 && (
              <p className="text-xs text-slate-500 mt-4 text-center">
                Affichage des 10 postes avec le plus de candidats
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecrutementStats;
